import { useEffect, useState } from "react";
import useUserInfoStore, {
  UserStore,
} from "@features/mySph/store/useUserInfoStore";
import { getUserType } from "@features/mySph/utils/helpers";
import {
  UserRegistrationStatusEnum,
  UserTypeEnum,
} from "@features/mySph/utils/types";
import { isEmpty } from "lodash-es";

/**
 * Hook to get the user info flags and the userInfo object from the store.
 * Notes:
 * As userInfo object use in client side, it will cause hydration mismatch issue if we directly use it in the component.
 * So, we use this hook to get the userInfo object from the store and set the various user type info flags.
 * @returns Various user type info flags and the userInfo object from the store.
 */
export const useUserInfo = () => {
  const { userInfo, isAuthenticated } = useUserInfoStore(
    (state: UserStore) => ({
      userInfo: state.userInfo,
      isAuthenticated: state.isAuthenticated,
    })
  );

  const [isAnonymous, setIsAnonymous] = useState<boolean>(true);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [isSubscriber, setIsSubscriber] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userType, setUserType] = useState<UserTypeEnum>(
    UserTypeEnum.ANONYMOUS
  );
  const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
  const [displayName, setDisplayName] = useState<string | undefined>();
  const [loginId, setLoginId] = useState<string | undefined>(undefined);
  const [visitorCat, setVisitorCat] = useState<number | undefined>(undefined);
  const [userStatus, setUserStatus] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!userInfo) return;
    if (!userInfo.loginId) return;
    if (!userInfo.visitorId) return;

    const userType = getUserType(userInfo?.serviceFlags || []);

    setIsAnonymous(userType === UserTypeEnum.ANONYMOUS);
    setIsRegistered(userType === UserTypeEnum.REGISTERED);
    setIsSubscriber(userType === UserTypeEnum.SUBSCRIBER);
    setIsLoggedIn(!isEmpty(userInfo.loginId));
    setUserType(userType || UserTypeEnum.ANONYMOUS);
    setIsVerified(userInfo.userStatus === UserRegistrationStatusEnum.VERIFIED);
    setVisitorId(userInfo.visitorId);
    setLoginId(userInfo.loginId);
    setUserStatus(userInfo.userStatus);
    setDisplayName(userInfo.displayName);
    setVisitorCat(userInfo.visitorcat);
  }, [userInfo]);

  return {
    isSubscriber,
    isAnonymous,
    isRegistered,
    isVerified,
    isUnverified: !isVerified,
    isLoggedIn,
    userType,
    visitorId,
    displayName,
    loginId,
    visitorCat,
    userInfo,
    userStatus,
    isAuthenticated,
  };
};
